// PointClickCare progress-note write-back (pcc-api: /pcc-integration/progress-notes). All user-facing strings say "PointClickCare".
import { HTTP_FORBIDDEN } from "./pccSyncJobs";
const BASE = () => window.CONFIG.pcc_integration_api;
const PROGRESS_NOTES_PATH = "/progress-notes";
const NOTE_TYPES_PATH = "/progress-notes/types";

const HTTP_BAD_REQUEST = 400;
// send-call statuses that leave the PCC outcome unknown: no response (client/network timeout), API error after the
// PCC post, gateway errors
const HTTP_NO_RESPONSE = 0;
const HTTP_INTERNAL_SERVER_ERROR = 500;
const HTTP_BAD_GATEWAY = 502;
const HTTP_SERVICE_UNAVAILABLE = 503;
const HTTP_GATEWAY_TIMEOUT = 504;
const UNCONFIRMED_HTTP_STATUSES = [HTTP_NO_RESPONSE, HTTP_INTERNAL_SERVER_ERROR, HTTP_BAD_GATEWAY, HTTP_SERVICE_UNAVAILABLE, HTTP_GATEWAY_TIMEOUT];

export const PROGRESS_NOTE_SOURCE_ENTITY = "infectionCase";
export const INFECTION_NOTE_TYPE = "Infection Note"; // the only note type infection cases are sent as
export const DEFAULT_SECTION_NAME = "Note Text"; // used when the chosen type defines no sections
export const PROVIDER_SECTION_NAME = "Provider"; // section pcc-api adds for the sending provider; shown as the User column, not in details
export const DESIGNATION_OPTIONS = ["RN", "RPN", "LPN", "NP", "MD", "PSW"]; // user may also type their own
export const DESIGNATION_MAX_LENGTH = 50; // matches pcc-api progressNoteRequestBodySchema (longer → 400)

const RESIDENT_CLIENT_TYPE = "Client"; // same value as CreateInfectionCase clientTypeMap.Resident

export const SendStatus = { Posted: "posted", Failed: "failed" };
const FailReason = {
  UnknownNoteType: "unknownNoteType",
  InvalidSection: "invalidSection",
  ForbiddenCharacters: "forbiddenCharacters",
  DuplicateExternalId: "duplicateExternalId",
  RateLimited: "rateLimited",
  PccUnavailable: "pccUnavailable",
  PccError: "pccError",
  PccNoResponse: "pccNoResponse",
  SendInProgress: "sendInProgress",
  MappingNotPersisted: "mappingNotPersisted"
};
// pcc-api resends an unconfirmed note under its original externalId when the note is unchanged, so the dialog locks
// the note after these and sending it again cannot duplicate it
const UNCONFIRMED_REASONS = [FailReason.PccNoResponse, FailReason.MappingNotPersisted, FailReason.SendInProgress];
const SAFE_RESEND_MESSAGE = "PointClickCare did not confirm the note. Send it again; this will not create a duplicate.";
const FAIL_MESSAGES = {
  [FailReason.UnknownNoteType]: "That note type is not configured for this facility in PointClickCare.",
  [FailReason.InvalidSection]: `The note section is not valid for the ${INFECTION_NOTE_TYPE} type.`,
  [FailReason.ForbiddenCharacters]: "The note type or section name contains characters PointClickCare does not accept (< > & \" ').",
  // PCC already holds a note under this externalId from elsewhere; pcc-api never reuses that row, so a resend gets a new one
  [FailReason.DuplicateExternalId]: "PointClickCare reported a conflict with an existing note. Please try again.",
  [FailReason.RateLimited]: "PointClickCare rate limit reached. Please try again later.",
  [FailReason.PccUnavailable]: "PointClickCare is currently unavailable. Please try again later.",
  [FailReason.PccError]: "PointClickCare rejected the note.",
  [FailReason.PccNoResponse]: SAFE_RESEND_MESSAGE,
  [FailReason.SendInProgress]: "This note is already being sent to PointClickCare. Wait a moment, then send it again if needed; this will not create a duplicate.",
  [FailReason.MappingNotPersisted]: "The note reached PointClickCare but HealthConnex could not record it. Send it again to finish recording it; this will not create a duplicate."
};
const GENERIC_ERROR = "The note could not be sent to PointClickCare.";

/** a resident of a PointClickCare Marketplace facility — the only clients progress notes can be sent for */
export function isPccNoteClient(configStore, client) {
  return !!client && client.clientType === RESIDENT_CLIENT_TYPE && configStore.isPccMarketplaceFacility(client.facId);
}

export async function fetchNoteTypes(http, clientId) {
  const response = await http.get(`${BASE()}${NOTE_TYPES_PATH}`, { params: { clientId } });
  return response.body.noteTypes || [];
}

/** the facility's own spelling of the Infection Note type (case-insensitive, trimmed), else undefined */
export function findInfectionNoteType(noteTypes) {
  return (noteTypes || []).find(t => typeof t.noteType === "string" && t.noteType.trim().toLowerCase() === INFECTION_NOTE_TYPE.toLowerCase());
}

/** GET a page of notes sent for one source: { content, totalElements } */
export async function listProgressNotes(http, { clientId, sourceId, page, size, includeUnconfirmed }) {
  const response = await http.get(`${BASE()}${PROGRESS_NOTES_PATH}`, {
    params: { clientId, sourceEntity: PROGRESS_NOTE_SOURCE_ENTITY, sourceId, page, size, includeUnconfirmed }
  });
  const { content, totalElements } = response.body;
  return { content, totalElements };
}

/** body: { clientId, sourceId, noteType, sections: [{ name, value }], effectiveDate, designation } */
export async function sendProgressNote(http, body) {
  const response = await http.post(`${BASE()}${PROGRESS_NOTES_PATH}`, JSON.stringify({ ...body, sourceEntity: PROGRESS_NOTE_SOURCE_ENTITY }));
  return response.body;
}

export function describeSendResult(result) {
  if (result && result.status === SendStatus.Posted) {
    // alreadyPosted: an earlier unconfirmed attempt of this note had reached PointClickCare
    return { type: "success", message: "Progress note sent to PointClickCare." };
  }
  const reason = result && result.reason;
  return { type: "error", message: FAIL_MESSAGES[reason] || GENERIC_ERROR };
}

/** a failed send whose note may already be in PointClickCare */
export function isUnconfirmedSendResult(result) {
  return !!result && result.status === SendStatus.Failed && UNCONFIRMED_REASONS.includes(result.reason);
}

/** a send call that failed at the HTTP level without telling whether the note reached PointClickCare — also a
 * rejection with no numeric status (aborted request, thrown Error) */
export function isUnconfirmedSendError(response) {
  return !response || typeof response.status !== "number" || UNCONFIRMED_HTTP_STATUSES.includes(response.status);
}

/** HTTP-level failures of the send call: outcome-unknown statuses get the resend message */
export function describeSendHttpError(response) {
  return isUnconfirmedSendError(response) ? { type: "error", message: SAFE_RESEND_MESSAGE } : describeHttpError(response);
}

/** HTTP-level failures (400 ineligible, 403, 404, 500) — `response` is the vue-resource error response */
export function describeHttpError(response) {
  const body = (response && response.body) || {};
  if (response && response.status === HTTP_BAD_REQUEST && body.reason) {
    return { type: "error", message: "This resident is not eligible for PointClickCare progress notes." };
  }
  if (response && response.status === HTTP_FORBIDDEN) {
    return { type: "error", message: "You are not permitted to send notes to PointClickCare for this facility." };
  }
  return { type: "error", message: GENERIC_ERROR };
}
